import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, forkJoin, of, catchError, map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SkeletonModule } from 'primeng/skeleton';

import { DrawerModule } from 'primeng/drawer';

import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EquipamentoService,
  PostoService,
  PropriedadeEquipService,
} from '@core/services/domain-services';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { EquipamentosNavComponent } from '../equipamentos-nav/equipamentos-nav.component';
import {
  Equipamento,
  EnumEstadoEquipamento,
  PropriedadeEquipamento,
} from '@core/models/models';
import { formatNomeCampo } from '@shared/utils/format-nome-campo.util';
import {
  EquipamentoKpiGridComponent,
  EquipamentoKpiItem,
} from '../components/equipamento-kpi-grid/equipamento-kpi-grid.component';
import { EquipamentoMaintenanceTabComponent } from '../components/equipamento-maintenance-tab/equipamento-maintenance-tab.component';
import { NovoEquipamentoComponent } from '../novo-equipamento/novo-equipamento.component';
import { EquipmentPhotoComponent } from '../components/equipment-photo/equipment-photo.component';
import { CategoriaEquipService } from '@core/services/domain-services';
import { TiposEquipamentoComponent } from '../tipos-equipamento/tipos-equipamento.component';
import { EquipamentoFilterStateService, EquipamentoFilters, filtrarEquipamentos, filtrosApiEquipamentos } from '../services/equipamento-filter-state.service';

const TIPO_UTENSILIOS = 3;

@Component({
  selector: 'app-utensilios-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    SkeletonModule,

    TooltipModule,
    DrawerModule,
    EmptyStateComponent,
    PaginatorControlComponent,
    BreadcrumbComponent,
    EquipamentosNavComponent,
    EquipamentoKpiGridComponent,
    EquipmentPhotoComponent,
    EquipamentoMaintenanceTabComponent,
    NovoEquipamentoComponent,
    TiposEquipamentoComponent,
  ],

  templateUrl: './utensilios.component.html',
  styleUrl: './utensilios.component.scss',
})
export class UtensiliosPageComponent implements OnInit {
  @ViewChild('rowMenu') rowMenu!: any;

  private readonly svc = inject(EquipamentoService);
  private readonly postoSvc = inject(PostoService);
  private readonly propriedadeSvc = inject(PropriedadeEquipService);
  private readonly fb = inject(FormBuilder);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly categoriaSvc = inject(CategoriaEquipService);
  private readonly filterSvc = inject(EquipamentoFilterStateService);

  readonly postoNomes = new Map<string, string>();
  equipamentos: Equipamento[] = [];
  pageFirst = 0;
  pageSize = 10;
  today = new Date();
  loading = false;
  loadingPropriedades = false;
  menuItems: MenuItem[] = [];
  showCreateDrawer = false;
  activeSection: 'list' | 'configuration' = 'list';
  totalRecords = 0;
  navTotal: number | null = null;
  IdCategoriaEquipamento: number | null = null;
  private activeFilters: EquipamentoFilters = {};

  readonly formatNomeCampo = formatNomeCampo;

  readonly estadoOptions = [
    { label: 'Todos os estados', value: null },
    { label: 'Bom Estado', value: EnumEstadoEquipamento.BOM },
    { label: 'Inactivo', value: EnumEstadoEquipamento.INATIVO },
    { label: 'Destruído', value: EnumEstadoEquipamento.DESTRUIDO },
  ];

  ngOnInit(): void {
    this.postoSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        const items = res?.data ?? res ?? [];
        if (Array.isArray(items)) {
          for (const p of items) {
            const id = p?.id ?? p?.Id;
            const nome = p?.nome ?? p?.Nome ?? p?.designacao ?? p?.Designacao;
            if (id != null && nome) this.postoNomes.set(String(id), String(nome));
          }
          this.cdr.markForCheck();
        }
      });

    this.filterSvc.filters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.activeFilters = filters;
        this.pageFirst = 0;
        this.load();
      });
  }

  load(): void {
    this.loading = true;
    const pageNumber = Math.floor(this.pageFirst / this.pageSize) + 1;

    this.svc
      .getAll({
        ...filtrosApiEquipamentos(this.activeFilters),
        IdTipoEquipamento: TIPO_UTENSILIOS,
        pageNumber,
        pageSize: Object.keys(this.activeFilters).length ? 1000 : this.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.equipamentos = response.data ?? [];
          this.carregarNomesPostos(this.equipamentos);
          this.totalRecords = response.totalRecords ?? this.equipamentos.length;
          this.navTotal = this.totalRecords;

          if (
            this.equipamentos.length === 0 &&
            this.totalRecords > 0 &&
            this.pageFirst >= this.totalRecords
          ) {
            this.pageFirst = Math.max(0, this.pageFirst - this.pageSize);
            this.load();
            return;
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.equipamentos = [];
          this.totalRecords = 0;
          this.navTotal = 0;
          this.loading = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro ao carregar',
            detail: 'Não foi possível carregar a listagem de equipamentos.',
            life: 3500,
          });
          this.cdr.detectChanges();
        },
      });
  }

  get filteredEquipamentos(): Equipamento[] {
    return filtrarEquipamentos(this.equipamentos, this.activeFilters);
  }

  get paginatedEquipamentos(): Equipamento[] {
    return this.equipamentos;
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const nextRows = event.rows ?? this.pageSize;
    this.pageFirst = nextRows !== this.pageSize ? 0 : (event.first ?? 0);
    this.pageSize = nextRows;
    this.load();
  }

  get kpiItems(): EquipamentoKpiItem[] {
    return [
      {
        icon: 'pi pi-warehouse',
        iconClass: 'total',
        value: this.kpiTotal,
        label: 'Total Utensílios',
      },
      {
        icon: 'ri-checkbox-circle-line',
        iconClass: 'bom',
        value: this.kpiBom,
        label: 'Activos (pág.)',
        valueClass: 'bom',
      },
      {
        icon: 'ri-pause-circle-line',
        iconClass: 'inativo',
        value: this.kpiInativo,
        label: 'Inactivos (pág.)',
        valueClass: 'inativo',
      },
      {
        icon: 'ri-money-dollar-circle-line',
        iconClass: 'valor',
        value: this.formatKz(this.kpiValor),
        label: 'Valor Total (pág.)',
        wide: true,
      },
    ];
  }

  get kpiTotal(): number {
    return this.totalRecords;
  }
  get maintenanceCount(): number {
    return this.maintenanceApiCount;
  }
  maintenanceApiCount = 0;
  get kpiBom(): number {
    return this.paginatedEquipamentos.filter((e) => Number(e.estado) === 1).length;
  }
  get kpiInativo(): number {
    return this.paginatedEquipamentos.filter((e) => Number(e.estado) === 3).length;
  }
  get kpiValor(): number {
    return this.paginatedEquipamentos.reduce((a, e) => a + (e.valorAquisicao ?? 0), 0);
  }

  getPropriedadeValor(e: Equipamento, nome: string): string {
    return (
      e.propriedades?.find((p) => p.nome.toUpperCase() === nome.toUpperCase())
        ?.valor ?? ''
    );
  }

  verDetalhe(e: Equipamento): void {
    if (e.id) this.router.navigate(['/admin/equipamentos', e.id]);
  }

  openCreateDrawer(): void {
    if (this.IdCategoriaEquipamento !== null) {
      this.showCreateDrawer = true;
      return;
    }

    this.categoriaSvc
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.IdCategoriaEquipamento =
            response.data?.find((category) =>
              (category.designacao ?? '').toUpperCase().includes('UTENS'),
            )?.id ?? null;
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.warn('Categorias indisponíveis:', error?.status);
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
      });
  }

  onEquipmentSaved(): void {
    this.showCreateDrawer = false;
    this.pageFirst = 0;
    this.msg.add({
      severity: 'success',
      summary: 'Registado',
      detail: 'Utensílio registado com sucesso.',
      life: 3000,
    });
    this.load();
  }

  openRowMenu(event: Event, e: Equipamento): void {
    this.menuItems = [
      {
        icon: 'pi pi-eye',
        label: 'Ver Detalhe',
        command: () => this.verDetalhe(e),
      },
      {
        icon: 'pi pi-wrench',
        visible: !this.isEquipamentoInativo(e),
        label: 'Gerir equipamento',
        command: () =>
          this.router.navigate(['/admin/equipamentos', e.id], {
            queryParams: { tab: 'manutencao', acao: 'registar' },
          }),
      },
      {
        icon: 'pi pi-pencil',
        label: 'Editar',
        command: () =>
          this.router.navigate(['/admin/equipamentos', e.id, 'editar']),
      },
      { separator: true },
      {
        icon: 'pi pi-trash',
        label: 'Eliminar',
        styleClass: 'menu-danger',
        command: () => this.confirmDelete(e),
      },
    ];
    this.rowMenu.toggle(event);
  }

  confirmDelete(e: Equipamento): void {
    this.confirm.confirm({
      header: 'Eliminar Utensílio',
      message: `O utensílio "${e.codigoInterno}" será eliminado. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!e.id) return;
        this.svc
          .delete(e.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Utensílio eliminado.',
                life: 3000,
              });
              this.load();
            },
          });
      },
    });
  }

  private isEquipamentoInativo(e: Equipamento): boolean {
    if (e.ativo === false) return true;
    const estado = String(e.estado ?? '')
      .trim()
      .toUpperCase();
    return ['INATIVO', 'INACTIVO', '3'].includes(estado);
  }

  getEstadoClass(estado?: any): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'badge-bom';
    if (val === 'DESTRUIDO' || val === '2') return 'badge-destruido';
    if (val === 'INATIVO' || val === '3') return 'badge-inativo';
    return '';
  }

  getEstadoLabel(estado?: any): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'Bom';
    if (val === 'DESTRUIDO' || val === '2') return 'Destruído';
    if (val === 'INATIVO' || val === '3') return 'Inactivo';
    return val || '—';
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  getPostoAtribuido(e: Equipamento): string {
    const equipamento = e as any;
    const postoNome =
      (equipamento.posto?.nome && String(equipamento.posto.nome).trim()) ||
      (equipamento.posto?.Nome && String(equipamento.posto.Nome).trim()) ||
      equipamento.posto?.nomePosto ||
      equipamento.posto?.NomePosto ||
      equipamento.posto?.designacao ||
      equipamento.postoNome ||
      equipamento.nomePosto ||
      equipamento.PostoNome;

    const rawPostoId =
      equipamento.idPosto ??
      equipamento.IdPosto ??
      equipamento.postoId ??
      equipamento.PostoId ??
      (equipamento.posto?.id > 0 ? equipamento.posto?.id : null) ??
      this.getPropriedadeValor(e, 'POSTO_ATRIBUIDO') ??
      this.getPropriedadeValor(e, 'POSTO');

    const idStr = rawPostoId != null && rawPostoId !== '' ? String(rawPostoId) : null;
    const nomeFromMap = idStr ? this.postoNomes.get(idStr) : null;

    return postoNome || nomeFromMap || '—';
  }

  private carregarNomesPostos(equipamentos: Equipamento[]): void {
    const ids = new Set(
      equipamentos
        .map((equipamento) => {
          const item = equipamento as any;
          return (
            item.idPosto ??
            item.IdPosto ??
            item.postoId ??
            item.PostoId ??
            (item.posto?.id > 0 ? item.posto?.id : null) ??
            this.getPropriedadeValor(item, 'POSTO_ATRIBUIDO') ??
            this.getPropriedadeValor(item, 'POSTO')
          );
        })
        .filter((id) => id != null && id !== '')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const idsPorCarregar = [...ids].filter((id) => !this.postoNomes.has(String(id)));
    if (!idsPorCarregar.length) return;

    forkJoin(
      idsPorCarregar.map((id) =>
        this.postoSvc.getById(id).pipe(
          catchError(() => of(null)),
          map((posto: any) => ({ requestedId: id, posto })),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultados: any[]) => {
        for (const { requestedId, posto } of resultados) {
          const data = (posto as any)?.data ?? posto;
          const id = data?.id ?? data?.Id ?? requestedId;
          const nome = data?.nome ?? data?.Nome ?? data?.designacao ?? data?.Designacao;
          if (id != null && nome) this.postoNomes.set(String(id), String(nome));
        }
        this.cdr.markForCheck();
      });
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('pt-AO');
    } catch {
      return d;
    }
  }
}
